import { Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { authFetch } from '@/api/http-client';
import { AuthShell } from '@/features/auth/components/AuthShell';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { ROTAS, destinoSeguro, loginComRetorno } from '@/shared/lib/rotas';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';

/**
 * Ponte do app para o site.
 *
 * O app mobile autentica por bearer token e o site por cookie httpOnly — não há
 * login compartilhado. Quando o veterinário toca em "A discussão deve ser aberta
 * pelo site", o app pede um token de uso único a `POST /v1/auth/handoff-web` e
 * abre `/entrar-app?token=…&next=/discussoes/nova` no navegador in-app. Esta
 * página troca esse token por cookie de sessão e some do caminho.
 *
 * Três detalhes que, se saírem, quebram o fluxo de formas confusas:
 *
 * 1. A rota está em `ROTAS_PUBLICAS`. Sem isso, o `GET /me` respondendo 401 —
 *    que aqui é o estado NORMAL de quem acabou de chegar — faria o AuthProvider
 *    redirecionar para o `/login` antes da troca sequer acontecer.
 *
 * 2. `recarregar()` antes de navegar. O cookie chega no `Set-Cookie` do
 *    `verify`, mas o cache do `/me` ainda guarda o 401 de quando a página
 *    montou; navegar direto cairia no `PrivateRoute` com sessão "ausente".
 *
 * 3. `replace: true`. O token vale por uma sessão inteira e está na URL — o
 *    "voltar" do navegador in-app não pode trazer ele de volta.
 */
export function EntrarPeloAppPage() {
  const [busca] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { recarregar } = useAuth();

  const token = busca.get('token');
  const destino = destinoSeguro(location.search, ROTAS.discussaoNova);

  const [erro, setErro] = useState<string | undefined>();

  // O token é de uso único: em StrictMode o efeito roda duas vezes no dev, e a
  // segunda consumiria um token já gasto e mostraria erro numa entrada que deu
  // certo.
  const jaTrocou = useRef(false);

  useEffect(() => {
    if (!token || jaTrocou.current) return;
    jaTrocou.current = true;

    void (async () => {
      try {
        await authFetch('/one-time-token/verify', { token });
        await recarregar();
        navigate(destino, { replace: true });
      } catch {
        // Token expirado, já usado ou adulterado chegam todos como 400. Não vale
        // distinguir: a saída é a mesma nos três casos, e detalhar diria a quem
        // está testando token qual deles chegou mais perto.
        setErro('Este link de acesso expirou ou já foi usado. Entre com seu e-mail e senha para continuar.');
      }
    })();
  }, [token, destino, navigate, recarregar]);

  if (!token || erro) {
    return (
      <AuthShell descricao="Entrar pelo aplicativo">
        <div className="space-y-4">
          <Alert variant="destructive">
            <AlertDescription className="text-sm">
              {erro ?? 'Link de acesso inválido ou incompleto.'}
            </AlertDescription>
          </Alert>
          <Button asChild className="w-full bg-brand text-brand-foreground hover:bg-brand/90">
            <Link to={loginComRetorno(destino)}>Ir para o login</Link>
          </Button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell descricao="Entrando…">
      <div className="flex items-center justify-center gap-3 py-8 text-sm text-brand">
        <Loader2 className="h-4 w-4 animate-spin" />
        Conectando sua conta…
      </div>
    </AuthShell>
  );
}
